import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

// Toast message type determines appearance
const TOAST_TYPES = {
  SUCCESS: "success",
  ERROR: "error",
  INFO: "info",
  WARNING: "warning",
};

// Component for a single toast notification
const Toast = ({ id, message, type, onClose, duration = 3000 }) => {
  useEffect(() => {
    // Auto-close the toast after duration
    const timer = setTimeout(() => {
      onClose(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, onClose, duration]);

  // Colors and icons based on toast type
  const toastStyles = {
    [TOAST_TYPES.SUCCESS]: {
      bg: "bg-green-100",
      border: "border-green-500",
      text: "text-green-800",
      icon: faCheckCircle,
      iconColor: "text-green-500",
    },
    [TOAST_TYPES.ERROR]: {
      bg: "bg-red-100",
      border: "border-red-500",
      text: "text-red-800",
      icon: faExclamationTriangle,
      iconColor: "text-red-500",
    },
    [TOAST_TYPES.WARNING]: {
      bg: "bg-yellow-100",
      border: "border-yellow-500",
      text: "text-yellow-800",
      icon: faExclamationTriangle,
      iconColor: "text-yellow-500",
    },
    [TOAST_TYPES.INFO]: {
      bg: "bg-blue-100",
      border: "border-blue-500",
      text: "text-blue-800",
      icon: faInfoCircle,
      iconColor: "text-blue-500",
    },
  };

  const styles = toastStyles[type] || toastStyles[TOAST_TYPES.INFO];

  return (
    <div
      className={`${styles.bg} ${styles.text} ${styles.border} border-l-4 p-4 mb-3 rounded shadow-md animate-fadeInRight relative`}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <FontAwesomeIcon
            icon={styles.icon}
            className={`${styles.iconColor} mr-3 text-lg`}
          />
        </div>
        <div className="flex-1">
          <p className="text-sm">{message}</p>
        </div>
        <button
          onClick={() => onClose(id)}
          className={`ml-4 ${styles.text} hover:opacity-75 focus:outline-none`}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
    </div>
  );
};

// Toast container component that manages multiple toasts
const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  // Add a new toast
  const addToast = (message, type = TOAST_TYPES.INFO, duration = 3000) => {
    const id = Date.now();
    setToasts((currentToasts) => [
      ...currentToasts,
      { id, message, type, duration },
    ]);
    return id;
  };

  // Remove a toast by id
  const removeToast = (id) => {
    setToasts((currentToasts) =>
      currentToasts.filter((toast) => toast.id !== id)
    );
  };

  // Expose methods to window so they can be called from anywhere
  useEffect(() => {
    window.toast = {
      success: (message, duration) =>
        addToast(message, TOAST_TYPES.SUCCESS, duration),
      error: (message, duration) =>
        addToast(message, TOAST_TYPES.ERROR, duration),
      info: (message, duration) =>
        addToast(message, TOAST_TYPES.INFO, duration),
      warning: (message, duration) =>
        addToast(message, TOAST_TYPES.WARNING, duration),
      remove: removeToast,
    };

    return () => {
      delete window.toast;
    };
  }, []);

  if (!toasts.length) return null;

  return (
    <div className="fixed top-4 right-4 z-50 max-w-md w-full">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          id={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={removeToast}
        />
      ))}
    </div>
  );
};

export default ToastContainer;
